import { Chip } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import { RoutePaths } from "@utils/constants/routes";
import { Link } from "react-router-dom";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import type { OfferObject } from "@/libs/generated/types";
import { TABLE_COLUMN_SIZING } from "@/utils/constants/table-column-sizing";
import { formatEuro } from "@/utils/delivery-pricing";
import { getOfferColorByStatus } from "@/utils/Offer";

import { ActionCell } from "./action-cell";

type TableColumnAlign = "left" | "center" | "right";
type ColumnDefWithAlign<TData> = ColumnDef<TData> & { align?: TableColumnAlign };

export const offerColumns: ColumnDefWithAlign<OfferObject>[] = [
  {
    id: "driver",
    meta: { label: "Nom et Prénom" },
    accessorKey: "driverId",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.xl,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => {
      const driverId = row.original.driverId.toString();
      const firstName = row.original.driver?.firstName;
      const lastName = row.original.driver?.lastName;
      const proposedByAdminId = row.original.proposedByAdminId?.toString() ?? "";
      const proposedByDriver = row.original.proposedByDriver;

      const routePachAdminRank = RoutePaths.admins;
      const driverLink = `/admin/drivers/drivers-table/accepted-requests/${driverId}`;
      const adminLink = `${RoutePaths.admin}${RoutePaths.manageAdmins}${routePachAdminRank}?focus=${proposedByAdminId}`;

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Link to={proposedByDriver ? driverLink : adminLink}>
            {proposedByDriver ? (
              <Chip label="DRIVER" color="primary" />
            ) : (
              <Chip label="ADMIN" color="info" />
            )}
          </Link>

          <Link style={{ color: "blue", textDecoration: "underline" }} to={driverLink}>
            {`${firstName} ${lastName}`}
          </Link>
        </div>
      );
    },
  },
  {
    id: "demandedPrice",
    accessorKey: "demandedPrice",
    meta: { label: "Prix" },
    enableSorting: true,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.md,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Prix" />,
    cell: ({ row }) => <>{formatEuro(row.original.demandedPrice)}</>,
  },
  {
    id: "status",
    accessorKey: "status",
    meta: { label: "Status" },
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.md,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Chip label={row.original.status} color={getOfferColorByStatus(row.original.status)} />
    ),
  },
  {
    id: "actions",
    meta: { label: "Action", align: "center" },
    accessorKey: "id",
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_SIZING.lg,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => (
      <ActionCell
        deliveryId={Number(row.original.deliveryId)}
        id={row.original.id}
        driverId={row.original.driverId}
        status={row.original.status}
      />
    ),
  },
];
